import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "@/i18n";
import { LEGAL } from "@/data/legal";
import { FAQ_CATEGORIES } from "@/data/site";
import api from "@/lib/api";

/* Friendly labels for breadcrumb URL segments. Anything not in this
   map falls back to Title-Cased segment text (e.g. "blog" → "Blog"). */
const BREADCRUMB_LABELS = {
  "challenges": "Challenges",
  "how-it-works": "How It Works",
  "payout-protector": "Payout Protector",
  "calculator": "Profit Calculator",
  "compare": "Compare Platforms",
  "platforms": "Trading Platforms",
  "ctrader": "cTrader",
  "dxtrade": "DXTrade",
  "matchtrader": "Match-Trader",
  "gooeypro": "GooeyPro",
  "web": "Web Trading",
  "mobile": "Mobile Trading",
  "partner": "Partner Program",
  "affiliates": "Affiliates",
  "influencers": "Influencer Affiliates",
  "reviews": "Trader Reviews",
  "blog": "Blog",
  "faq": "FAQ",
  "about": "About",
  "contact": "Contact",
  "legal": "Legal",
  "terms": "Terms of Service",
  "privacy": "Privacy Policy",
  "cookies": "Cookie Policy",
  "refund": "Refund Policy",
};

function labelFor(seg) {
  return (
    BREADCRUMB_LABELS[seg] ||
    seg.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/* -- DOM helpers -- */
function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  }
  return el;
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertHreflangLinks(path) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
  const origin = `${window.location.protocol}//${window.location.host}`;
  LANGUAGES.forEach((l) => {
    const link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", l.code);
    link.setAttribute("href", l.code === "en" ? `${origin}${path}` : `${origin}${path}?lang=${l.code}`);
    document.head.appendChild(link);
  });
  const xd = document.createElement("link");
  xd.setAttribute("rel", "alternate");
  xd.setAttribute("hreflang", "x-default");
  xd.setAttribute("href", `${origin}${path}`);
  document.head.appendChild(xd);
}

/* Replace or insert a JSON-LD script with a fixed id. */
function upsertJsonLd(id, jsonObj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(jsonObj);
}
function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

/* -- Per-route copy. These override the index.html defaults. -- */
const ROUTE_SEO = {
  "/": null, // index.html defaults are best for landing — admin can also override via /api/seo
  "/challenges": {
    title: "Funded Trader Challenges — Instant, 1-Step & 2-Step · OFunded",
    description: "Buy a funded prop trading challenge starting at $49. Choose instant funding, 1-step, or 2-step evaluation up to $250,000. Up to 90% Account Gains, transparent rules, 24-48h onboarding.",
    keywords: "prop firm challenge, funded account, instant funding, 1 step prop firm, 2 step prop firm, $50K funded, $100K funded, $250K funded, cTrader prop firm, DXTrade prop firm",
  },
  "/about": {
    title: "About OFunded — Dubai-based Prop Trading Firm · OMNI Markets LLC",
    description: "OFunded is a Dubai-based proprietary trading firm operated by OMNI Markets LLC. Read about our mission, transparent rules, and global trader community.",
    keywords: "about OFunded, OMNI Markets, Dubai prop firm, prop trading company, prop firm UAE",
  },
  "/faq": {
    title: "OFunded FAQ — How Prop Trading Challenges & Payouts Work",
    description: "Answers to the most common questions about OFunded's prop trading challenges, profit splits, KYC, payouts, allowed strategies and platform compatibility.",
    keywords: "prop firm FAQ, OFunded FAQ, funded trader questions, prop firm payouts, KYC prop firm",
  },
  "/partner": {
    title: "Become an OFunded Partner — 10% Lifetime + 10% Audience Discount",
    description: "Flat 10% lifetime commission on every referred funded challenge, plus a negotiable 1-month discount code (up to 10%) for your audience. Real-time dashboards, monthly payouts.",
    keywords: "OFunded partner, OFunded affiliate, prop firm partner program, lifetime commission, audience discount code",
  },
  "/how-it-works": {
    title: "How OFunded Works — From Challenge to First Payout · OFunded",
    description: "Step-by-step guide to OFunded's prop trading evaluation: pick a challenge, pass the eval, get funded in 24-48h, withdraw up to 90% of your gains.",
    keywords: "how prop firm works, OFunded process, funded trader evaluation, prop firm payout process",
  },
  "/general-rules": {
    title: "OFunded Trading Rules — Drawdown, Targets & Payouts Explained",
    description: "Complete OFunded rulebook covering 5% daily / 8% overall drawdown, profit targets, allowed strategies, payout schedule and prohibited trading conduct.",
    keywords: "OFunded rules, prop firm drawdown rules, prop firm profit target, news trading prop firm",
  },
  "/affiliates": {
    title: "OFunded Partner Program — 10% Lifetime + 10% Audience Discount · OFunded",
    description: "Earn a flat 10% lifetime commission on every referred funded challenge, plus a negotiable 1-month discount code (up to 10%) for your audience. Real-time dashboard, monthly payouts.",
    keywords: "OFunded affiliate, OFunded partner program, prop firm affiliate, lifetime commission, prop firm referral",
  },
  "/platforms/compare": {
    title: "Compare cTrader vs DXTrade vs MatchTrader vs GooeyPro · OFunded",
    description: "Side-by-side comparison of OFunded's four trading platforms — execution model, DOM, algos, FIX API, mobile apps, copy-trading and execution latency.",
    keywords: "cTrader vs DXTrade, MatchTrader comparison, GooeyPro review, prop firm platform comparison",
  },
  "/payout-protector": {
    title: "Payout Protector — Insure Your First Payout · OFunded",
    description: "Add Payout Protector at checkout to refund your challenge fee if your funded account breaches drawdown before your first monthly payout cycle.",
    keywords: "Payout Protector, prop firm insurance, OFunded add-on, payout guarantee",
  },
  "/platforms/ctrader": {
    title: "cTrader at OFunded — ECN Execution & Native DOM · OFunded",
    description: "Trade an OFunded funded account on cTrader. Native depth of market, cBots automation, FIX API, sub-8ms execution latency, identical rules across all platforms.",
    keywords: "cTrader prop firm, cTrader funded account, OFunded cTrader, cTrader ECN prop firm",
  },
  "/platforms/dxtrade": {
    title: "DXTrade at OFunded — Browser-Native Multi-Asset · OFunded",
    description: "DXTrade web and mobile clients for OFunded funded traders. No install, multi-asset coverage, TradingView charts, identical OFunded rules.",
    keywords: "DXTrade prop firm, DXTrade funded account, OFunded DXTrade, browser trading platform prop firm",
  },
  "/platforms/matchtrader": {
    title: "MatchTrader at OFunded — Mobile-First Matching Engine · OFunded",
    description: "Trade your OFunded simulated account on MatchTrader. Modern matching engine, built-in copy-trading, one-tap risk controls, 99.99% uptime.",
    keywords: "MatchTrader prop firm, MatchTrader funded account, OFunded MatchTrader, copy-trading prop firm",
  },
  "/platforms/gooeypro": {
    title: "GooeyPro at OFunded — Cloud-Powered Low-Latency Execution · OFunded",
    description: "GooeyPro for systematic traders at OFunded. Sub-5ms latency, cloud-native engine, algo-friendly APIs, distributed matching.",
    keywords: "GooeyPro prop firm, GooeyPro funded account, OFunded GooeyPro, algo trading prop firm",
  },
  "/platforms/web": {
    title: "Web Trading at OFunded — Trade From Any Browser · OFunded",
    description: "All four OFunded platforms offer a browser-based client. No install, full charting and order management, identical rules across cTrader, DXTrade, MatchTrader and GooeyPro.",
    keywords: "web trading prop firm, browser-based trading, no install trading platform, OFunded web trading",
  },
  "/platforms/mobile": {
    title: "Mobile Trading Apps at OFunded — iOS & Android · OFunded",
    description: "Native iOS and Android apps for cTrader, DXTrade and MatchTrader. Full charting, biometric login, push alerts for drawdown thresholds.",
    keywords: "mobile prop firm trading, iOS trading app, Android trading app, OFunded mobile",
  },
  "/affiliates/influencers": {
    title: "OFunded Influencer Program — Custom Commissions for Creators · OFunded",
    description: "Dedicated influencer track for content creators. Custom commission rates, account manager, exclusive promo codes, ready-to-publish asset library.",
    keywords: "trader influencer program, OFunded creator, prop firm sponsorship, trader content creator",
  },
  "/affiliates/dashboard": {
    title: "Affiliate Dashboard — Track Clicks, Conversions & Payouts · OFunded",
    description: "Real-time click tracking, conversion stats, balance, payout history and creative downloads. Sign in or apply to the OFunded affiliate program.",
    keywords: "affiliate dashboard, prop firm commission tracking, OFunded affiliate portal",
  },
  "/calculator": {
    title: "Prop Firm Profit Calculator — Project Your Monthly Payout · OFunded",
    description: "Pick an account size and target monthly return; see your projected payout after OFunded's 90% gain share. Free, no sign-up required.",
    keywords: "prop firm calculator, funded trader payout calculator, OFunded calculator, profit split calculator",
  },
  "/blog": {
    title: "OFunded Blog & Insights — Trader Stories, Strategy & Industry Analysis · OFunded",
    description: "Trader interviews, strategy breakdowns, drawdown model deep-dives and prop trading industry analysis. New posts every Tuesday.",
    keywords: "prop firm blog, funded trader interviews, prop trading insights, OFunded blog",
  },
  "/contact": {
    title: "Contact OFunded — Support, Affiliates, Press & Refunds · OFunded",
    description: "Reach the OFunded team. Dedicated support, affiliates, press and refunds inboxes. We respond within 4 business hours.",
    keywords: "OFunded contact, prop firm support, OFunded refund contact, OFunded press",
  },
  "/reviews": {
    title: "OFunded Reviews — Real Funded-Trader Stories · OFunded",
    description: "Read verified reviews and video stories from OFunded-funded traders worldwide. Real payouts, real testimonials, no incentivised reviews.",
    keywords: "OFunded reviews, prop firm reviews, funded trader testimonials, OFunded trustpilot",
  },
};

const LEGAL_SEO = {
  privacy: {
    title: "Privacy Policy · OFunded",
    description: "How OFunded (OMNI Markets LLC) collects, processes and protects your personal data — KYC, cookies, analytics and your GDPR rights.",
    keywords: "OFunded privacy policy, prop firm privacy, GDPR prop firm, data protection trading firm",
  },
  terms: {
    title: "Terms & Conditions · OFunded",
    description: "OFunded Terms & Conditions covering the simulated trading evaluation, account purchase, prohibited conduct, payouts and dispute resolution.",
    keywords: "OFunded terms, prop firm terms, funded account agreement, simulated trading terms",
  },
  cookies: {
    title: "Cookie Policy · OFunded",
    description: "Details on how OFunded uses cookies for analytics, performance, language preference and security. Manage your cookie preferences.",
    keywords: "OFunded cookies, prop firm cookie policy, GDPR cookies",
  },
  refund: {
    title: "Refund Policy · OFunded",
    description: "OFunded refund policy — 14-day cooling-off on unused evaluations, duplicate-purchase refunds and technical-failure compensation. How to request a refund.",
    keywords: "OFunded refund, prop firm refund policy, evaluation refund, prop firm chargeback policy",
  },
};

export default function SEOHead() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    const origin = `${window.location.protocol}//${window.location.host}`;
    const pathname = location.pathname;
    // Build per-route metadata
    let meta = ROUTE_SEO[pathname] || null;
    let isLegal = false;
    if (pathname.startsWith("/legal/")) {
      const slug = pathname.split("/")[2];
      if (LEGAL_SEO[slug]) {
        meta = LEGAL_SEO[slug];
        isLegal = true;
      }
    }

    // Apply meta tags (title / description / keywords / OG / twitter)
    if (meta) {
      document.title = meta.title;
      upsertMeta('meta[name="description"]', { name: "description", content: meta.description });
      upsertMeta('meta[name="keywords"]', { name: "keywords", content: meta.keywords });
      upsertMeta('meta[property="og:title"]', { property: "og:title", content: meta.title });
      upsertMeta('meta[property="og:description"]', { property: "og:description", content: meta.description });
      upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: meta.title });
      upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: meta.description });
    }

    // Per-route canonical + hreflang
    upsertCanonical(`${origin}${pathname}`);
    upsertHreflangLinks(pathname);
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: window.location.href });
    upsertMeta('meta[property="og:locale"]', {
      property: "og:locale",
      content: ({ en: "en_US", ar: "ar_SA", fr: "fr_FR", es: "es_ES", de: "de_DE", pt: "pt_PT", so: "so_SO" })[i18n.language] || "en_US",
    });

    // Structured Data — BreadcrumbList on every non-root page. Uses
    // friendly labels (Title Case + acronym preserved) so the breadcrumb
    // rendered in SERPs matches the on-site nav copy and improves
    // sitelink eligibility.
    if (pathname !== "/") {
      const parts = pathname.split("/").filter(Boolean);
      const items = [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
      ];
      let acc = "";
      parts.forEach((p, idx) => {
        acc += `/${p}`;
        items.push({
          "@type": "ListItem",
          position: idx + 2,
          name: labelFor(p),
          item: `${origin}${acc}`,
        });
      });
      upsertJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
      });
    } else {
      removeJsonLd("ld-breadcrumb");
    }

    // Structured Data — Article schema on legal pages
    if (isLegal) {
      const slug = pathname.split("/")[2];
      const doc = LEGAL[slug];
      if (doc) {
        upsertJsonLd("ld-article", {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: doc.title,
          datePublished: "2024-01-01",
          dateModified: "2026-02-20",
          author: { "@type": "Organization", name: "OFunded — OMNI Markets LLC" },
          publisher: {
            "@type": "Organization",
            name: "OFunded",
            logo: { "@type": "ImageObject", url: `${origin}/favicon.svg` },
          },
          description: doc.intro,
          inLanguage: i18n.language,
        });
      }
    } else {
      removeJsonLd("ld-article");
    }

    // Structured Data — WebPage on every route. Wires page → site,
    // surfaces title/description in SERPs, and feeds Google Discover.
    if (meta || pathname === "/") {
      upsertJsonLd("ld-webpage", {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: meta?.title || document.title,
        description: meta?.description || document.querySelector('meta[name="description"]')?.content,
        url: `${origin}${pathname}`,
        inLanguage: i18n.language,
        isPartOf: { "@type": "WebSite", name: "OFunded", url: `${origin}/` },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: `${origin}/og-image.png`,
          width: 1200,
          height: 630,
        },
        publisher: {
          "@type": "Organization",
          name: "OFunded",
          logo: { "@type": "ImageObject", url: `${origin}/favicon.svg` },
        },
      });
    } else {
      removeJsonLd("ld-webpage");
    }

    // Structured Data — Service schema on each platform sub-page so
    // Google can show platform-specific rich SERP entries.
    if (pathname.startsWith("/platforms/")) {
      const platformMap = {
        "/platforms/ctrader":     { name: "cTrader at OFunded",     desc: "Trade an OFunded simulated funded account on cTrader with ECN execution, native DOM, cBots and FIX API." },
        "/platforms/dxtrade":     { name: "DXTrade at OFunded",     desc: "DXTrade web + mobile clients for OFunded funded traders. Browser-native, multi-asset coverage, TradingView charts." },
        "/platforms/matchtrader": { name: "MatchTrader at OFunded", desc: "MatchTrader for OFunded funded traders. Modern matching engine, copy-trading, mobile-first design, 99.99% uptime." },
        "/platforms/gooeypro":    { name: "GooeyPro at OFunded",    desc: "GooeyPro for systematic traders at OFunded. Cloud-powered, sub-5ms latency, algo-friendly APIs." },
        "/platforms/web":         { name: "Web Trading at OFunded", desc: "Browser-based clients for all four OFunded platforms. No install, full charting and order management." },
        "/platforms/mobile":      { name: "Mobile Trading at OFunded", desc: "Native iOS + Android trading apps for cTrader, DXTrade and MatchTrader at OFunded." },
        "/platforms/compare":     { name: "Compare OFunded Platforms", desc: "Side-by-side comparison of OFunded's four trading platforms — execution model, DOM, algos, FIX API." },
      };
      const sv = platformMap[pathname];
      if (sv) {
        upsertJsonLd("ld-service", {
          "@context": "https://schema.org",
          "@type": "Service",
          name: sv.name,
          description: sv.desc,
          serviceType: "Prop trading platform access",
          provider: { "@type": "Organization", name: "OFunded", url: `${origin}/` },
          areaServed: "Worldwide (excluding OFAC-sanctioned jurisdictions)",
          audience: { "@type": "Audience", audienceType: "Forex, indices, gold and crypto traders" },
          url: `${origin}${pathname}`,
        });
      } else {
        removeJsonLd("ld-service");
      }
    } else {
      removeJsonLd("ld-service");
    }

    // Structured Data — FAQPage schema on the FAQ route (or home, since
    // the FAQ section sits inline on the home page too)
    if (pathname === "/faq" || pathname === "/") {
      try {
        // Flatten FAQ_CATEGORIES → list of {q, a}
        const flat = (FAQ_CATEGORIES || []).flatMap((cat) => cat.items || []);
        const mainEntity = flat.slice(0, 30).map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        }));
        if (mainEntity.length) {
          upsertJsonLd("ld-faq", {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity,
          });
        }
      } catch (err) {
        // safety net — never break the page over SEO
        if (process.env.NODE_ENV === "development") {
          if (process.env.NODE_ENV !== "production") console.debug("SEOHead: failed to build FAQPage schema", err);
        }
      }
    } else {
      removeJsonLd("ld-faq");
    }
  }, [location.pathname, i18n.language, params.slug]);

  // Backend SEO override (admin-managed). Runs once.
  useEffect(() => {
    api.get("/seo").then(({ data }) => {
      if (!data) return;
      if (location.pathname !== "/") return; // backend override is for home only
      if (data.title) document.title = data.title;
      if (data.description) upsertMeta('meta[name="description"]', { name: "description", content: data.description });
      if (data.keywords) upsertMeta('meta[name="keywords"]', { name: "keywords", content: data.keywords });
      if (data.robots) upsertMeta('meta[name="robots"]', { name: "robots", content: data.robots });
      if (data.og_title) upsertMeta('meta[property="og:title"]', { property: "og:title", content: data.og_title });
      if (data.og_description) upsertMeta('meta[property="og:description"]', { property: "og:description", content: data.og_description });
      if (data.og_image) upsertMeta('meta[property="og:image"]', { property: "og:image", content: data.og_image });
      upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
      if (data.canonical_url) upsertCanonical(data.canonical_url);
      if (data.twitter_card) upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: data.twitter_card });
      if (data.og_title) upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: data.og_title });
      if (data.og_description) upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: data.og_description });
      if (data.og_image) upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: data.og_image });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
